/**
 * TrustSection — Stats + Partner Logos + Credibility
 * Design: Light gray background, navy stats, green accents
 */

import React, { useEffect, useRef, useState } from "react";
import { Shield, Award, Clock, Users, TrendingUp } from "lucide-react";

const stats = [
  { icon: TrendingUp, value: 50, suffix: "M+", prefix: "$", label: "Total Funded", color: "#48d480" },
  { icon: Users, value: 10000, suffix: "+", prefix: "", label: "Businesses Funded", color: "#48d480" },
  { icon: Clock, value: 24, suffix: "-48hrs", prefix: "", label: "Funding Speed", color: "#48d480" },
  { icon: Award, value: 99.8, suffix: "%", prefix: "", label: "Success Rate", color: "#48d480" },
  { icon: Shield, value: 10, suffix: "+", prefix: "", label: "Years Experience", color: "#48d480" },
];

function useCountUp(target, duration = 1500, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

// Helper function to format count display
const formatCountDisplay = (stat, count, animate) => {
  if (!animate) return "0";
  if (stat.value < 100 && stat.value % 1 !== 0) {
    return count.toFixed(1);
  }
  return count.toLocaleString();
};

function StatCard({ stat, animate }) {
  const count = useCountUp(stat.value, 1500, animate);
  const Icon = stat.icon;
  return (
    <div className="flex flex-col items-center text-center p-6 fade-up">
      <div className="w-14 h-14 rounded-full bg-[#0f2d52]/10 flex items-center justify-center mb-4">
        <Icon size={26} className="text-[#0f2d52]" />
      </div>
      <div
        style={{ fontFamily: "'Montserrat', sans-serif" }}
        className="text-3xl md:text-4xl font-black text-[#0f2d52] mb-1"
      >
        {stat.prefix}
        {formatCountDisplay(stat, count, animate)}
        {stat.suffix}
      </div>
      <div
        style={{ fontFamily: "'Inter', sans-serif" }}
        className="text-gray-500 text-sm font-medium"
      >
        {stat.label}
      </div>
      <div className="mt-3 h-0.5 w-10 bg-[#48d480] rounded" />
    </div>
  );
}

const badges = [
  { label: "SBA Certified Partner", icon: "S" },
  { label: "BBB Accredited", icon: "A+" },
  { label: "FDIC Insured Partners", icon: "F" },
  { label: "SSL Secured", icon: "L" },
  { label: "Industry Award 2025", icon: "W" },
  { label: "Regulatory Compliant", icon: "C" },
];

export default function TrustSection() {
  const ref = useRef(null);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setAnimate(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} id="trust" data-testid="trust-section" className="py-16 md:py-20 bg-[#f8fafc]">
      <div className="container">
        {/* Section Label */}
        <div className="text-center mb-12 fade-up">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="h-px w-8 bg-[#48d480]" />
            <span className="section-label">Why Choose Us</span>
            <div className="h-px w-8 bg-[#48d480]" />
          </div>
          <h2
            style={{ fontFamily: "'Montserrat', sans-serif" }}
            className="text-3xl md:text-4xl font-bold text-[#0f2d52]"
          >
            Trusted by Thousands of Businesses
          </h2>
          <p
            style={{ fontFamily: "'Inter', sans-serif" }}
            className="text-gray-500 mt-3 max-w-xl mx-auto"
          >
            Legacy Triangle has helped businesses across America secure the funding they need to grow, expand, and thrive.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-14">
          {stats.map((stat) => (
            <StatCard key={stat.label} stat={stat} animate={animate} />
          ))}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-200 mb-10" />

        {/* Trust Badges */}
        <div className="fade-up">
          <p
            style={{ fontFamily: "'Inter', sans-serif" }}
            className="text-center text-xs font-bold tracking-widest uppercase text-gray-400 mb-6"
          >
            Certifications & Partnerships
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {badges.map((badge) => (
              <div
                key={badge.label}
                className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-4 py-2 shadow-sm hover:shadow-md transition-shadow"
              >
                <span className="w-6 h-6 rounded-full bg-[#0f2d52] text-white text-xs font-bold flex items-center justify-center">
                  {badge.icon}
                </span>
                <span
                  style={{ fontFamily: "'Inter', sans-serif" }}
                  className="text-[#0f2d52] text-xs font-semibold"
                >
                  {badge.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
